import AbstractCarousel from '../Carousel';
import { ProductCardBuilder } from '../ProductCard/ProductCardBuilder';
import { generateMediaObserverCallback, generateMediaObserverOptions } from './utils';

class ProductRec extends AbstractCarousel {
  constructor({ gallery, prefixClassName, gap, features, extraToggleClass, recData, resultId, numResults, podId }) {
    super({ gallery, prefixClassName, gap, features, extraToggleClass });

    // Store gallery reference for container attribute population
    this.gallery = gallery;

    // recData would be the data fetched from Constructor API for product recommendations
    this.data = recData || [];
    this.resultId = resultId || '';
    this.numResults = numResults || 0;
    this.podId = podId || '';
    this.templateIdentifier =
      gallery.querySelector('.product-rec-c-carousel[data-section-id]')?.id.replace('-carousel', '') || '';
    this.sectionId = gallery.querySelector('.product-rec-c-carousel[data-section-id]')?.dataset?.sectionId || '';
    // Number of product card images to show based on screen width
    this.thresholdCards = window.innerWidth > 989 ? Number(this.carousel.dataset?.numberOfSlides) - 1 || 3 : 1;
  }

  init() {
    // Set Product Rec. as populated or initialized
    this.gallery.setAttribute('data-populated', 'true');

    // Populate container-level Constructor tracking attributes
    this.#populateContainerAttributes();

    // Build product cards with actual data from Constructor API
    // Get tracking labels from carousel container data attributes (set from Liquid section settings)
    const carouselContainer = this.gallery.querySelector('.product-rec-c-carousel');
    const trackingCreative = carouselContainer?.getAttribute('data-promo-creative') || 'Product Recommendations';

    const productCardNodes = [];

    // Build product cards with actual data from Constructor API
    this.data.forEach((product, idx) => {
      const builder = new ProductCardBuilder(product.value, product.data?.product_type)
        .makeExtraBaseClass('product-rec-c-carousel')
        .makeNode(this.carousel)
        .makeCarouselItemNode(this.templateIdentifier, idx + 1)
        .makeLocation(`${this.sectionId}_${this.podId}`)
        .makeUrl(product.data.url)
        .makeMedia(product.data.media)
        .makeVariants(product.variations)
        .makeProductId(product.data.id)
        .makeVariantId()
        .makePlaceholderTransform()
        .selectColor()
        .makePrices()
        .makeOptions()
        .makeReviews(product.data?.reviews_count, product.data?.reviews_average)
        .makeQuickAdd()
        .makeCardImageHidden(idx > this.thresholdCards)
        .makeBadge(product.data.selected_badge)
        .makeBadgeVisible()
        // .makeVideo(product.data.original_video_source)
        .makeConstructorRecommendation()
        .makePromoTracking(trackingCreative, `${this.templateIdentifier}-product-${idx + 1}`)
        .setConstructorAttributes(product)
        .setPodId(this.podId)
        .makeSwatches();

      builder.build();
      productCardNodes.push(builder.productCardNode);
    });

    setTimeout(() => {
      // Build markers based on the number of products available that come from Constructor
      this.#buildMarkers();
      // Update markers NodeList after building them
      this.setMarkers();
      // Set images NodeList after building product cards
      this.setImages();
      // Set observers for lazy loading images
      this.#setObservers();
      // Bind all basic events
      this.bindAbstractCarouselEvents();
      // Drop the arrows when everything already fits on screen
      this.toggleArrows();
      // Wire up impression tracking for each product card
      this.#setupImpressionTracking(productCardNodes);
    }, 0);
  }

  #setupImpressionTracking(productCardNodes) {
    const productCardImpressionData = new Map();
    const listName = `recommendations_${this.podId}`;

    productCardNodes.forEach((productCardNode, idx) => {
      const product = this.data[idx];
      const firstVariation = product.variations?.[0];
      const barcode = firstVariation?.data?.barcode || '';
      const priceUS = firstVariation?.data?.priceUS || 0;
      const compareAtPriceUS = firstVariation?.data?.compareAtPriceUS || 0;
      productCardImpressionData.set(productCardNode, {
        id: product.data?.id || '',
        style: barcode,
        list: listName,
        subcategory: product.data?.product_type || '',
        name: (product.value || '').replace(/((CLEARANCE)|(PLACEHOLDER))( - )/, ''),
        price: Number(priceUS).toFixed(2),
        originalPrice: compareAtPriceUS > 0 ? Number(compareAtPriceUS).toFixed(2) : Number(priceUS).toFixed(2),
        newArrival: (product.data?.tags || []).includes('New Arrival'),
        isMarkedDown: compareAtPriceUS > 0 && priceUS < compareAtPriceUS,
        hasReviews: false,
        reviewCount: 0,
        reviewAverage: 0,
        showsQuadpay: !!document.querySelector('[data-klarna-visible]'),
        almostGone: false,
        position: idx + 1,
      });
    });

    const impressionObserver = new IntersectionObserver(
      (entries) => {
        const visibleProducts = [];
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const data = productCardImpressionData.get(entry.target);
            if (data) {
              visibleProducts.push(data);
              productCardImpressionData.delete(entry.target);
              impressionObserver.unobserve(entry.target);
            }
          }
        });
        if (visibleProducts.length > 0) {
          const dataLayer = window.dataLayer || [];
          dataLayer.push({
            event: 'impressions',
            ecommerce: {
              impressions: { products: visibleProducts },
            },
          });
        }
        if (productCardImpressionData.size === 0) {
          impressionObserver.disconnect();
        }
      },
      { root: null, rootMargin: '0px', threshold: 0.1 },
    );

    productCardImpressionData.forEach((_, productCardEl) => impressionObserver.observe(productCardEl));
  }

  toggleArrows() {
    if (!this.prevBtn || !this.nextBtn) return;

    const scrollable = this.carousel.scrollWidth > this.carousel.clientWidth;
    this.prevBtn.disabled = true;
    this.nextBtn.disabled = !scrollable;
  }

  #buildMarkers() {
    // Implement marker building logic if needed
    const numberOfSlides = Number(this.markersContainer.dataset.numberOfSlides);
    for (let idx = 0; idx <= this.data.length - numberOfSlides; idx++) {
      const marker = document.createElement('li');
      const count = idx + 1;
      marker.innerHTML = `
        <a href="#carousel_${this.templateIdentifier}_card_${count}" class="carousel-c-scroll__marker ${
          idx == 0 ? 'carousel-c-scroll__marker--active' : ''
        }">
          <span class="carousel-c-scroll__dot">${count}</span>
        </a>
      `;
      this.markersContainer.appendChild(marker);
    }
  }

  setImages() {
    this.images = this.carousel.querySelectorAll('.product-rec-c-carousel__product-card-images');
  }

  #setObservers() {
    // Injects an observer for fetching image media whenever are visible by target
    const imageObserverCallback = generateMediaObserverCallback();
    const imageObserverOptions = generateMediaObserverOptions(this.carousel);
    this.images.forEach((imageItem) => {
      const observer = new IntersectionObserver(imageObserverCallback, imageObserverOptions);
      observer.observe(imageItem);
    });
  }

  #populateContainerAttributes() {
    const container = this.gallery?.querySelector('[data-cnstrc-recommendations]');
    if (container) {
      container.setAttribute('data-cnstrc-result-id', this.resultId);
      container.setAttribute('data-cnstrc-num-results', this.numResults);
    }
  }

  reset() {
    // detaches all event listeners and comes back to zero the carousel scrolling
    this.closeAbstractCarouselEvents();
    this.resetNavigation();
  }
}

export default ProductRec;
